package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.billing.PlanCheck
import com.evoapps.evoshift.domain.billing.PlanResource
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.model.InviteDetails
import com.evoapps.evoshift.domain.usecase.AcceptInviteUseCase
import com.evoapps.evoshift.domain.usecase.CreateStarterScheduleUseCase
import com.evoapps.evoshift.domain.usecase.CreateWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.GetOwnerUseCase
import com.evoapps.evoshift.domain.usecase.OpeningHours
import com.evoapps.evoshift.domain.usecase.StarterScheduleNames
import com.evoapps.evoshift.domain.usecase.UpdateOwnerBusinessNameUseCase
import com.evoapps.evoshift.domain.usecase.ValidateInviteUseCase
import com.evoapps.evoshift.domain.usecase.auth.CompleteSetupUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.auth.SignOutUseCase
import com.evoapps.evoshift.domain.usecase.billing.CheckPlanLimitUseCase
import com.evoapps.evoshift.domain.usecase.settings.SaveSalesTargetsUseCase
import com.evoapps.evoshift.domain.usecase.settings.UpdateWorkplaceSettingsUseCase
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.UpdateShiftTemplateUseCase
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import com.evoapps.evoshift.domain.util.normalizeTime
import evoshift.composeapp.generated.resources.error_save_failed
import com.evoapps.evoshift.domain.usecase.settings.UpdateWorkplaceNameUseCase
import com.evoapps.evoshift.saves.SaveOutcome
import com.evoapps.evoshift.saves.fold
import com.evoapps.evoshift.saves.runOrKeepOnDevice
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_business_name_required
import evoshift.composeapp.generated.resources.error_session_expired
import evoshift.composeapp.generated.resources.error_workplace_name_required
import evoshift.composeapp.generated.resources.onboarding_error_empty_code
import evoshift.composeapp.generated.resources.onboarding_hours_invalid
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** The onboarding steps (SH-38), EVoLabels' OnboardingStep for EVoShift. */
enum class OnboardingStep { WELCOME, ROLE, BUSINESS, WORKPLACE, HOURS, FINANCE, JOIN, DONE }

/** Which way the person goes: opening a business, joining one, or already part of one. */
enum class OnboardingPath { OWNER, JOIN, READY }

data class OnboardingUiState(
    val step: OnboardingStep = OnboardingStep.WELCOME,
    val path: OnboardingPath? = null,
    /** Reading the profile and owner document before the first step. */
    val isLoading: Boolean = true,
    /** An owner document exists already (e.g. a workplace was deleted): no business step. */
    val hasOwnerProfile: Boolean = false,
    val existingWorkplaceCount: Int = 0,
    val businessName: String = "",
    val workplaceName: String = "",
    /** Whose labour law, currency and time zone apply; Poland is the only market today. */
    val market: Market = Market.POLAND,
    val hours: OpeningHours = OpeningHours(),
    val createdWorkplaceId: String? = null,
    /** The name the workplace was created with (a later change renames it). */
    val createdWorkplaceName: String? = null,
    val savedBusinessName: String? = null,
    val starterScheduleCreated: Boolean = false,
    /** The created workplace as last read or changed here: the final review shows its defaults (SH-62). */
    val workplace: Workplace? = null,
    /** The starter shift templates as created, then as edited on the review. */
    val starterShifts: List<ShiftTemplate> = emptyList(),
    /** The day template the starter shifts make up; the weekly skeleton's open days point to it. */
    val starterDayTemplateId: String? = null,
    /** The answer to "will you plan labour cost against sales?" (SH-61); null until answered. */
    val plansFinances: Boolean? = null,
    /** The weekly sales plan, keyed "monday".."sunday" ([com.evoapps.evoshift.domain.model.WorkplaceFinancials.salesTargetKey]), as saved. */
    val salesTargets: Map<String, Double> = emptyMap(),
    /** The sales plan editor is open (after "yes", or to change it). */
    val isEditingSalesTargets: Boolean = false,
    val inviteCode: String = "",
    /** The invitation behind [inviteCode], once checked. */
    val invite: InviteDetails? = null,
    val joinedWorkplaceName: String? = null,
    val isSaving: Boolean = false,
    /** A problem with what was typed in the current step (shown under the field). */
    val fieldError: UiText? = null,
    /** Anything else (shown in a snackbar). */
    val error: UiText? = null,
    val planLimit: PlanCheck.LimitReached? = null,
    /** The profile shows a workplace: the dashboard can open (no bounce back to onboarding). */
    val isReady: Boolean = false,
    /** Setup is complete: leave for the dashboard. */
    val isFinished: Boolean = false
) {
    /** The ordered steps of the chosen path: navigation, progress and "Step X of Y" use it. */
    val steps: List<OnboardingStep>
        get() = when (path) {
            OnboardingPath.READY -> listOf(OnboardingStep.DONE)
            OnboardingPath.JOIN -> listOf(OnboardingStep.WELCOME, OnboardingStep.ROLE, OnboardingStep.JOIN, OnboardingStep.DONE)
            // Unknown yet: the owner path, the longer one, so progress does not jump.
            OnboardingPath.OWNER, null -> listOfNotNull(
                OnboardingStep.WELCOME,
                OnboardingStep.ROLE,
                OnboardingStep.BUSINESS.takeUnless { hasOwnerProfile },
                OnboardingStep.WORKPLACE,
                OnboardingStep.HOURS,
                OnboardingStep.FINANCE,
                OnboardingStep.DONE
            )
        }

    /** The ISO days (1 = Monday) the weekly skeleton runs the starter day on. */
    val starterOpenDays: Set<Int>
        get() = starterDayTemplateId?.let { id -> workplace?.settings?.defaultWeeklySkeleton?.filterValues { it == id }?.keys }.orEmpty()

    val stepNumber: Int get() = steps.indexOf(step).coerceAtLeast(0) + 1
    val progress: Float get() = stepNumber.toFloat() / steps.size
    val canGoBack: Boolean get() = step != OnboardingStep.DONE && steps.indexOf(step) > 0 && !isSaving
}

/**
 * The first-run flow (SH-38): welcome, role, then for owners business, first workplace,
 * opening hours (starter shifts) and financial planning (the weekly sales plan, SH-61); for
 * invited people the invitation code; then done. No plan step (SH-222): the trial needs no
 * choice, and the paywall comes after it. Reuses the existing use cases (CreateWorkplaceUseCase, ValidateInviteUseCase,
 * AcceptInviteUseCase, CompleteSetupUseCase...) and replaces the old Setup and Onboarding
 * screens. Each step's data is saved when leaving it, so going back never creates twice.
 */
class OnboardingViewModel(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getOwner: GetOwnerUseCase,
    private val createWorkplace: CreateWorkplaceUseCase,
    private val updateWorkplaceName: UpdateWorkplaceNameUseCase,
    private val updateBusinessName: UpdateOwnerBusinessNameUseCase,
    private val createStarterSchedule: CreateStarterScheduleUseCase,
    private val validateInvite: ValidateInviteUseCase,
    private val acceptInvite: AcceptInviteUseCase,
    private val completeSetup: CompleteSetupUseCase,
    private val signOutUseCase: SignOutUseCase,
    private val getWorkplace: GetWorkplaceUseCase,
    private val updateWorkplaceSettings: UpdateWorkplaceSettingsUseCase,
    private val updateShiftTemplate: UpdateShiftTemplateUseCase,
    private val saveSalesTargets: SaveSalesTargetsUseCase,
    private val checkPlanLimit: CheckPlanLimitUseCase? = null,
    /** SH-148: the app's saves: a step's write runs to its end, one after another. */
    private val saves: com.evoapps.evoshift.saves.PendingSaves = com.evoapps.evoshift.saves.PendingSaves(),
    /** SH-186: offline, a plain write unanswered for a few seconds counts as kept on this device. */
    private val connectivity: com.evoapps.evoshift.connectivity.ConnectivityWatcher = com.evoapps.evoshift.connectivity.ManualConnectivity()
) : ViewModel() {
    private val _state = MutableStateFlow(OnboardingUiState())
    val state: StateFlow<OnboardingUiState> = _state.asStateFlow()

    init {
        viewModelScope.launch {
            val user = getCurrentUser().firstOrNull()
            val owner = user?.let { getOwner(it.uid).getOrNull() }
            val alreadyPartOfATeam = !user?.engagements.isNullOrEmpty()
            _state.update {
                it.copy(
                    isLoading = false,
                    hasOwnerProfile = owner != null,
                    existingWorkplaceCount = owner?.workplaceIds?.size ?: 0,
                    businessName = owner?.businessName.orEmpty(),
                    // Someone who already works somewhere (e.g. joined through an invitation link)
                    // only confirms; nothing to set up.
                    path = if (alreadyPartOfATeam) OnboardingPath.READY else it.path,
                    step = if (alreadyPartOfATeam) OnboardingStep.DONE else it.step
                )
            }
        }
        // Ready once the profile shows a workplace; the dashboard reads the same profile.
        viewModelScope.launch {
            getCurrentUser().collect { user ->
                _state.update { it.copy(isReady = !user?.engagements.isNullOrEmpty()) }
            }
        }
    }

    // --- Navigation -------------------------------------------------------------------------

    private fun go(step: OnboardingStep) = _state.update { it.copy(step = step, fieldError = null) }

    private fun nextStep() {
        val state = _state.value
        val steps = state.steps
        steps.getOrNull(steps.indexOf(state.step) + 1)?.let(::go)
    }

    fun start() = nextStep()

    fun back() {
        val state = _state.value
        if (!state.canGoBack) return
        state.steps.getOrNull(state.steps.indexOf(state.step) - 1)?.let(::go)
    }

    fun chooseOwner() {
        _state.update { it.copy(path = OnboardingPath.OWNER) }
        nextStep()
    }

    fun chooseJoin() {
        _state.update { it.copy(path = OnboardingPath.JOIN) }
        nextStep()
    }

    // --- Owner path ---------------------------------------------------------------------------

    fun updateBusinessName(value: String) = _state.update { it.copy(businessName = value, fieldError = null) }

    fun updateWorkplaceName(value: String) = _state.update { it.copy(workplaceName = value, fieldError = null) }

    fun submitBusiness() {
        val state = _state.value
        val name = state.businessName.trim()
        if (name.isBlank()) {
            _state.update { it.copy(fieldError = UiText.Resource(Res.string.error_business_name_required)) }
            return
        }
        // The owner document is written with the first workplace (CreateWorkplaceUseCase); a
        // rename after that goes to the owner document directly.
        val saved = state.savedBusinessName
        if (saved == null || saved == name) return nextStep()
        save(kept = true, then = {
            _state.update { it.copy(savedBusinessName = name) }
            nextStep()
        }) {
            updateBusinessName(uid(), name).getOrThrow()
        }
    }

    /**
     * Creates the workplace once (later a rename). [allDaySlotLabel] names its default availability
     * slot in the owner's language (SH-60); the screen resolves it, as it does the starter shift names.
     */
    fun submitWorkplace(allDaySlotLabel: String) {
        val state = _state.value
        val name = state.workplaceName.trim()
        if (name.isBlank()) {
            _state.update { it.copy(fieldError = UiText.Resource(Res.string.error_workplace_name_required)) }
            return
        }
        val createdId = state.createdWorkplaceId
        save {
            if (createdId == null) {
                val check = checkPlanLimit?.invoke(PlanResource.WORKPLACES, state.existingWorkplaceCount)
                if (check is PlanCheck.LimitReached) {
                    _state.update { it.copy(planLimit = check) }
                    return@save
                }
                val business = state.businessName.trim().takeUnless { state.hasOwnerProfile }
                val id = createWorkplace(uid(), name, business, allDaySlotLabel = allDaySlotLabel).getOrThrow()
                AppTelemetry.log(AnalyticsEvent.WorkplaceCreated)
                _state.update {
                    it.copy(createdWorkplaceId = id, createdWorkplaceName = name, savedBusinessName = business ?: it.savedBusinessName)
                }
                refreshWorkplace(id)
            } else if (name != state.createdWorkplaceName) {
                updateWorkplaceName(createdId, name).getOrThrow()
                _state.update { it.copy(createdWorkplaceName = name) }
            }
            nextStep()
        }
    }

    fun planLimitShown() = _state.update { it.copy(planLimit = null) }

    fun toggleDay(isoDay: Int) = updateHours {
        it.copy(openDays = if (isoDay in it.openDays) it.openDays - isoDay else it.openDays + isoDay)
    }

    /** Moves opening by [deltaMinutes] (the UI steps by half an hour), keeping it before closing. */
    fun shiftOpening(deltaMinutes: Int) = updateHours {
        it.copy(opensAtMinutes = (it.opensAtMinutes + deltaMinutes).coerceIn(0, it.closesAtMinutes - HALF_HOUR))
    }

    /** Moves closing by [deltaMinutes], up to midnight and after opening. */
    fun shiftClosing(deltaMinutes: Int) = updateHours {
        it.copy(closesAtMinutes = (it.closesAtMinutes + deltaMinutes).coerceIn(it.opensAtMinutes + HALF_HOUR, OpeningHours.MINUTES_PER_DAY))
    }

    private fun updateHours(change: (OpeningHours) -> OpeningHours) =
        _state.update { it.copy(hours = change(it.hours), fieldError = null) }

    /** Creates the starter shifts once (names in the owner's language come from the screen). */
    fun submitHours(names: StarterScheduleNames) {
        val state = _state.value
        if (!state.hours.isValid) {
            _state.update { it.copy(fieldError = UiText.Resource(Res.string.onboarding_hours_invalid)) }
            return
        }
        val workplaceId = state.createdWorkplaceId
        if (state.starterScheduleCreated || workplaceId == null) return nextStep()
        save {
            val schedule = createStarterSchedule(workplaceId, state.hours, names).getOrThrow()
            _state.update {
                it.copy(starterScheduleCreated = true, starterShifts = schedule.shiftTemplates, starterDayTemplateId = schedule.dayTemplateId)
            }
            refreshWorkplace(workplaceId) // the weekly skeleton now runs the starter day
            nextStep()
        }
    }

    fun skipHours() = nextStep()

    // --- Financial planning (SH-61) -------------------------------------------------------------

    /** Yes opens the weekly sales plan; no goes on without one (it can be added in the settings later). */
    fun answerFinancePlanning(plansFinances: Boolean) {
        _state.update { it.copy(plansFinances = plansFinances, isEditingSalesTargets = plansFinances) }
        if (!plansFinances) nextStep()
    }

    fun editSalesTargets() = _state.update { it.copy(isEditingSalesTargets = true) }

    fun closeSalesTargets() = _state.update { it.copy(isEditingSalesTargets = false) }

    /** Saves the sales plan ([targets] keyed "monday".."sunday"); on the finance step it then goes on. */
    fun submitSalesTargets(targets: Map<String, Double>) {
        val workplaceId = _state.value.createdWorkplaceId ?: return
        save(kept = true, then = {
            _state.update { it.copy(salesTargets = targets, plansFinances = true, isEditingSalesTargets = false) }
            if (_state.value.step == OnboardingStep.FINANCE) nextStep()
        }) {
            saveSalesTargets(workplaceId, targets).getOrThrow()
        }
    }

    // --- The workplace on the review (SH-63) -----------------------------------------------------

    /** Renames the created workplace from the review (optimistic, rolled back on failure). */
    fun renameWorkplace(name: String) {
        val trimmed = name.trim()
        val workplace = _state.value.workplace ?: return
        if (trimmed.isBlank() || trimmed == workplace.name) return
        val before = _state.value
        _state.update { it.copy(workplace = workplace.copy(name = trimmed), createdWorkplaceName = trimmed, workplaceName = trimmed) }
        viewModelScope.launch {
            updateWorkplaceName(workplace.id, trimmed).onFailure { e ->
                _state.update {
                    it.copy(
                        workplace = it.workplace?.copy(name = workplace.name),
                        createdWorkplaceName = before.createdWorkplaceName,
                        workplaceName = before.workplaceName,
                        error = UiText.Resource(Res.string.error_save_failed, e.toUiText())
                    )
                }
            }
        }
    }

    // --- Editing the defaults on the review (SH-62) -----------------------------------------------
    // Optimistic: the review shows the change at once and rolls it back if saving fails.

    /** A starter shift's name or times (the skeleton editor's dialog). */
    fun updateStarterShift(template: ShiftTemplate) {
        val workplaceId = _state.value.createdWorkplaceId ?: return
        val before = _state.value.starterShifts.firstOrNull { it.id == template.id } ?: return
        if (before == template) return
        replaceStarterShift(template)
        viewModelScope.launch {
            updateShiftTemplate(workplaceId, template).onFailure { e ->
                replaceStarterShift(before)
                _state.update { it.copy(error = UiText.Resource(Res.string.error_save_failed, e.toUiText())) }
            }
        }
    }

    private fun replaceStarterShift(template: ShiftTemplate) =
        _state.update { s -> s.copy(starterShifts = s.starterShifts.map { if (it.id == template.id) template else it }) }

    /** The days ([isoDays], 1 = Monday) the weekly skeleton runs the starter day on; at least one. */
    fun updateOpenDays(isoDays: Set<Int>) {
        val dayTemplateId = _state.value.starterDayTemplateId ?: return
        if (isoDays.isEmpty() || isoDays.any { it !in 1..7 }) {
            _state.update { it.copy(error = UiText.Resource(Res.string.onboarding_hours_invalid)) }
            return
        }
        modifySettings { settings ->
            settings.copy(
                defaultWeeklySkeleton = settings.defaultWeeklySkeleton.filterValues { it != dayTemplateId } +
                    isoDays.associateWith { dayTemplateId }
            )
        }
    }

    fun addAvailabilitySlot(label: String, startTime: String, endTime: String) = modifySettings { settings ->
        val slot = AvailabilitySlot(
            id = "slot_${AppClock.now().toEpochMilliseconds()}",
            label = label,
            startTime = startTime.normalizeTime(),
            endTime = endTime.normalizeTime()
        )
        settings.copy(definedAvailabilitySlots = settings.definedAvailabilitySlots + slot)
    }

    fun removeAvailabilitySlot(slotId: String) = modifySettings { settings ->
        settings.copy(definedAvailabilitySlots = settings.definedAvailabilitySlots.filterNot { it.id == slotId })
    }

    private fun modifySettings(change: (WorkplaceSettings) -> WorkplaceSettings) {
        val workplace = _state.value.workplace ?: return
        val updated = change(workplace.settings)
        if (updated == workplace.settings) return
        _state.update { it.copy(workplace = workplace.copy(settings = updated)) }
        viewModelScope.launch {
            updateWorkplaceSettings(workplace.id, updated).onFailure { e ->
                _state.update { it.copy(workplace = workplace, error = UiText.Resource(Res.string.error_save_failed, e.toUiText())) }
            }
        }
    }

    /** Reads the created workplace for the review; not fatal (the review then shows less). */
    private suspend fun refreshWorkplace(workplaceId: String) {
        getWorkplace(workplaceId).onSuccess { workplace -> _state.update { it.copy(workplace = workplace) } }
    }

    // --- Join path ----------------------------------------------------------------------------

    fun updateInviteCode(value: String) =
        _state.update { it.copy(inviteCode = value.uppercase().trim(), invite = null, fieldError = null) }

    fun checkInvite() {
        val code = _state.value.inviteCode
        if (code.isBlank()) {
            _state.update { it.copy(fieldError = UiText.Resource(Res.string.onboarding_error_empty_code)) }
            return
        }
        save(onError = { e -> _state.update { it.copy(fieldError = e.toInviteUiText()) } }) {
            val invite = validateInvite(code).getOrThrow()
            _state.update { it.copy(invite = invite) }
        }
    }

    fun join() {
        val state = _state.value
        val invite = state.invite ?: return checkInvite()
        save(onError = { e -> _state.update { it.copy(fieldError = e.toInviteUiText()) } }) {
            acceptInvite(state.inviteCode).getOrThrow()
            _state.update { it.copy(joinedWorkplaceName = invite.workplaceName) }
            nextStep()
        }
    }

    // --- Done -------------------------------------------------------------------------------

    /** Marks setup complete (CompleteSetupUseCase) and leaves for the dashboard. */
    fun finish() {
        if (!_state.value.isReady) return
        save(kept = true, then = { _state.update { it.copy(isFinished = true) } }) {
            completeSetup(uid()).getOrThrow()
        }
    }

    fun signOut() {
        viewModelScope.launch { signOutUseCase() }
    }

    fun errorShown() = _state.update { it.copy(error = null) }

    // --- Helpers --------------------------------------------------------------------------------

    private suspend fun uid(): String =
        getCurrentUser().first()?.uid ?: throw SessionExpired()

    private class SessionExpired : Exception("No signed-in user")

    /**
     * Runs [write] with the step's spinner on, in the app's saves (SH-148, one after another);
     * [then] follows a write that went through. SH-186: [kept] marks a plain Firestore write whose
     * answer the flow does not need (a rename, the sales plan, finishing): offline, after a few
     * seconds, the change is in this device's cache, the shell says so and [then] runs as if
     * saved. A write whose answer the next step needs (creating the workplace or its starter
     * shifts) waits for the connection, so nothing is created twice.
     */
    private fun save(
        onError: (Throwable) -> Unit = { e ->
            _state.update {
                it.copy(error = if (e is SessionExpired) UiText.Resource(Res.string.error_session_expired) else e.toUiText())
            }
        },
        kept: Boolean = false,
        then: () -> Unit = {},
        write: suspend () -> Unit
    ) {
        if (_state.value.isSaving) return
        _state.update { it.copy(isSaving = true, error = null, fieldError = null) }
        viewModelScope.launch {
            val outcome = if (kept) {
                saves.runOrKeepOnDevice(SAVE_KEY, connectivity.isOnline, queue = true) { runCatching { write() } }
            } else {
                saves.run(SAVE_KEY, queue = true) { runCatching { write() } }.fold({ SaveOutcome.Done(it) }, { SaveOutcome.Failed(it) })
            }
            _state.update { it.copy(isSaving = false) }
            outcome.fold(onSuccess = { then() }, onFailure = onError, onKept = then)
        }
    }

    private companion object {
        const val HALF_HOUR = 30
        /** The first-run flow's writes in [com.evoapps.evoshift.saves.PendingSaves]. */
        const val SAVE_KEY = "onboarding"
    }
}
